/**
 * FreeAgent Sync Button Component
 *
 * Provides a "Resync to FreeAgent" button with confirmation dialog
 * for the first 10 live pushes.
 */

import React, { useState, useEffect } from 'react';
import { Button, Modal, ModalDialog, DialogTitle, DialogContent, DialogActions, Typography, Alert, Stack } from '@mui/joy';
import { FaSync } from 'react-icons/fa';
import { IconWrapper } from './IconWrapper';
import {
  resyncInvoiceToFreeAgent,
  getSyncStatus,
  getFreeAgentOrgEligibility,
  type SyncResult,
  type FreeAgentOrgEligibility,
} from '../integrations/freeagentSync';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';

interface FreeAgentSyncButtonProps {
  documentId: string;
  organisationId?: string | null;
  onSyncComplete?: (result: SyncResult) => void;
  variant?: 'solid' | 'outlined' | 'soft' | 'plain';
  size?: 'sm' | 'md' | 'lg';
}

const CONFIRMATION_THRESHOLD = 10;
const STORAGE_KEY = 'freeagent_sync_count';

export const FreeAgentSyncButton: React.FC<FreeAgentSyncButtonProps> = ({
  documentId,
  organisationId,
  onSyncComplete,
  variant = 'outlined',
  size = 'sm',
}) => {
  const { user } = useSupabaseAuth();
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [syncStatus, setSyncStatus] = useState<{ syncStatus: string; syncError?: string } | null>(null);
  const [syncCount, setSyncCount] = useState(0);
  const [eligibility, setEligibility] = useState<FreeAgentOrgEligibility | null>(null);

  // Load sync count from localStorage
  useEffect(() => {
    const count = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
    setSyncCount(count);
  }, []);

  // Load current sync status
  useEffect(() => {
    loadSyncStatus();
  }, [documentId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const nextEligibility = await getFreeAgentOrgEligibility(organisationId);
        if (alive) setEligibility(nextEligibility);
      } catch (error) {
        console.error('Failed to load FreeAgent organisation eligibility:', error);
        if (alive) {
          setEligibility({
            eligible: false,
            reason: 'FreeAgent only accepts Keystone Glazing and Keystone South UK invoices. This invoice cannot be transmitted until it belongs to one of them.',
          });
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [organisationId]);

  const loadSyncStatus = async () => {
    try {
      const status = await getSyncStatus(documentId);
      setSyncStatus(status);
    } catch (error) {
      console.error('Failed to load sync status:', error);
    }
  };

  const isCheckingOrg = eligibility == null;
  const blockedReason = eligibility && !eligibility.eligible ? eligibility.reason : undefined;

  const handleResync = async () => {
    setShowConfirm(false);

    if (blockedReason) {
      alert(blockedReason);
      return;
    }

    setLoading(true);

    try {
      const result = await resyncInvoiceToFreeAgent(documentId, user?.id);

      // Increment sync count
      const newCount = syncCount + 1;
      setSyncCount(newCount);
      localStorage.setItem(STORAGE_KEY, newCount.toString());

      // Reload sync status
      await loadSyncStatus();

      // Show result to user
      if (!result.success || result.skipped) {
        const message = result.validationErrors?.length
          ? result.validationErrors.join('\n')
          : (result.error || 'This invoice was not sent to FreeAgent.');
        alert(`Cannot sync to FreeAgent:\n\n${message}`);
      } else if (!result.freeAgentUrl && !result.wasNew) {
        alert('Cannot sync to FreeAgent:\n\nThe invoice was not transmitted. Only Keystone Glazing invoices are sent to FreeAgent.');
      } else {
        alert(result.wasNew
          ? 'Invoice successfully synced to FreeAgent (new invoice created)'
          : 'Invoice successfully synced to FreeAgent'
        );
      }

      if (onSyncComplete) {
        onSyncComplete(result);
      }
    } catch (error: any) {
      console.error('Resync failed:', error);
      alert(`Failed to resync: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    if (blockedReason) {
      alert(blockedReason);
      return;
    }

    // Show confirmation for first 10 pushes
    if (syncCount < CONFIRMATION_THRESHOLD) {
      setShowConfirm(true);
    } else {
      handleResync();
    }
  };

  return (
    <>
      <Stack spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
        <Button
          variant={variant}
          size={size}
          startDecorator={<IconWrapper icon={FaSync} />}
          onClick={handleClick}
          loading={loading}
          disabled={loading || isCheckingOrg || !!blockedReason}
          color={syncStatus?.syncStatus === 'error' ? 'danger' : 'primary'}
        >
          {blockedReason
            ? 'Cannot sync to FreeAgent'
            : syncStatus?.syncStatus === 'error'
              ? 'Resync (Error)'
              : 'Resync to FreeAgent'}
        </Button>
      </Stack>

      {/* Confirmation Dialog */}
      <Modal open={showConfirm} onClose={() => setShowConfirm(false)}>
        <ModalDialog>
          <DialogTitle>Confirm FreeAgent Sync</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              This will resync the invoice to FreeAgent. Are you sure you want to continue?
            </Typography>
            {syncStatus?.syncError && (
              <Alert color="warning" sx={{ mb: 2 }}>
                <Typography level="body-sm">
                  Previous sync error: {syncStatus.syncError}
                </Typography>
              </Alert>
            )}
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              ({syncCount + 1} of {CONFIRMATION_THRESHOLD} confirmations required)
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setShowConfirm(false)}>
              Cancel
            </Button>
            <Button variant="solid" color="primary" onClick={handleResync} loading={loading}>
              Confirm Sync
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </>
  );
};
